import { debounce, throttle } from 'lodash';
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';

type FixedElementOnScrollPropsType = {
    /** The content to be rendered within the fixed element */
    children?: ReactNode | undefined;
    /** The interval in milliseconds to throttle scroll events (default: 16ms for ~60fps) */
    throttleInterval?: number | undefined;
    /** Whether to enable smooth transitions when the element's position changes (default: true) */
    transitionEnabled?: boolean | undefined;
    /**
     * Ref to a scrollable ancestor (e.g. a modal body or panel with `overflow: auto`) whose scroll
     * the element should follow. When omitted, the element follows the window scroll.
     */
    scrollContainerRef?: RefObject<HTMLElement | null> | undefined;
    /**
     * Distance in pixels from the top of the scroll viewport at which the element pins.
     * Defaults to 60 (the app header height) when following the window, and 0 when following a
     * scroll container. Pass a value to override either default.
     */
    topOffset?: number | undefined;
};

/**
 * A component that creates a fixed-position element that smoothly follows scroll.
 * The element maintains its position relative to the scroll viewport, confined to the parent.
 *
 * By default it follows the window scroll (offset by the app header). When a `scrollContainerRef`
 * is provided, it instead follows that container's scroll, which is required when the element lives
 * inside an element with its own `overflow: auto/scroll` (a modal body, an inner panel) — such a
 * container scrolls without firing a window scroll event.
 *
 * @example
 * ```tsx
 * <FixedElementOnScroll scrollContainerRef={modalBodyRef}>
 *   <div>This content will follow the container's scroll</div>
 * </FixedElementOnScroll>
 * ```
 *
 * @param props - Component props
 * @param props.children - Content to be rendered within the fixed element
 * @param props.throttleInterval - Interval in milliseconds to throttle scroll events (default: 16ms for ~60fps)
 * @param props.transitionEnabled - Whether to enable smooth transitions when the element's position changes (default: true)
 * @param props.scrollContainerRef - Scrollable ancestor to follow; omit to follow the window
 * @param props.topOffset - Pin offset from the top of the scroll viewport (default: 60 for window, 0 for a container)
 */
const FixedElementOnScroll = ({
    children,
    throttleInterval = 16,
    transitionEnabled = true,
    scrollContainerRef,
    topOffset,
}: FixedElementOnScrollPropsType) => {
    // Track vertical translation of the element
    const [translateY, setTranslateY] = useState(0);
    // Reference to the DOM element for position calculations
    const elementRef = useRef<HTMLDivElement>(null);
    // Mirror the current translation so measurements taken during resize can back it out of
    // getBoundingClientRect (which, unlike offsetTop, is affected by the active transform).
    const translateYRef = useRef(0);
    translateYRef.current = translateY;

    useEffect(() => {
        const container = scrollContainerRef?.current ?? null;
        // Pin offset: default to the app header height on the window, 0 inside a scroll container.
        const pinOffset = topOffset ?? (container ? 0 : 60);

        const getScrollTop = () => (container ? container.scrollTop : window.scrollY);

        /**
         * Gets the element's offset from the top of the scroll content, in the scroll source's
         * coordinate system so it can be compared against the scroll position. Both branches are
         * measured via bounding rects (backing out the active translation) rather than `offsetTop`:
         * `offsetTop` is relative to the nearest positioned ancestor, so when the element lives inside
         * a positioned wrapper (e.g. a react-resizable-panels pane) it reads ~0 and the element pins
         * far too early. The bounding-rect measurement yields the true page/container offset.
         */
        const measureInitialOffsetTop = () => {
            const element = elementRef.current;
            if (!element) {
                return 0;
            }
            if (!container) {
                return element.getBoundingClientRect().top - translateYRef.current + window.scrollY;
            }
            return (
                element.getBoundingClientRect().top -
                translateYRef.current -
                container.getBoundingClientRect().top +
                container.scrollTop
            );
        };

        // Store initial position of the element
        let initialOffsetTop = measureInitialOffsetTop();

        /**
         * Handles scroll events by updating the element's position
         * Throttled for performance optimization
         */
        const handleScroll = throttle(() => {
            const parentHeight = elementRef.current?.parentElement?.offsetHeight;
            if (!parentHeight) {
                return;
            }

            const scrollPos = getScrollTop();
            // Engage the pin exactly when the element would scroll past the pin line (its natural top
            // reaches `pinOffset`). Guarding on the bare offset instead makes the translation jump by
            // `pinOffset` at the boundary; subtracting it keeps the transition continuous.
            if (scrollPos < initialOffsetTop - pinOffset) {
                setTranslateY(0);
                return;
            }

            // Calculate new vertical translation, accounting for the pin offset
            const rawTranslateY = scrollPos + pinOffset - initialOffsetTop;

            // Calculate the maximum allowed translation before hitting parent bottom
            const parentBottom = parentHeight - elementRef.current.offsetHeight - pinOffset;
            const maxTranslateY = Math.max(0, parentBottom);

            // Limit the translation to keep the element within parent bounds
            const boundedTranslateY = Math.min(rawTranslateY, maxTranslateY);

            if (boundedTranslateY !== translateYRef.current) {
                setTranslateY(boundedTranslateY);
            }
        }, throttleInterval);

        /**
         * Handles window resize events by updating the element's initial position
         * Debounced to prevent excessive calculations during resize
         */
        const handleResize = debounce(() => {
            initialOffsetTop = measureInitialOffsetTop();
        }, 200);

        // Attach event listeners to the container when following it, otherwise to the window.
        const scrollTarget: HTMLElement | Window = container ?? window;
        scrollTarget.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        // Initial position calculation
        handleScroll();

        // Cleanup event listeners on component unmount
        return () => {
            scrollTarget.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [throttleInterval, scrollContainerRef, topOffset]);

    return (
        <div
            ref={elementRef}
            style={{
                transform: `translateY(${translateY}px)`,
                position: 'relative',
                zIndex: 2,
                willChange: 'transform', // Optimize for animations
                transition: transitionEnabled ? 'all 0.2s ease-in-out' : 'none', // Smooth transitions
            }}
        >
            {children}
        </div>
    );
};

export default FixedElementOnScroll;
